"use client";

import { useEffect, useRef, useState } from "react";
import type {
  Sam3Instance,
  Sam3Point,
  Sam3PointLabel,
  Sam3RoiPoint,
} from "@/lib/sam3Contract";
import {
  clampSam3PointToImageBounds,
  sanitizeSam3RoiPolygon,
} from "@/lib/sam3Geometry";

type Props = {
  imageUrl: string;
  width: number;
  height: number;
  mode: "point" | "lasso" | "text";
  points: Sam3Point[];
  lassoPolygon: Sam3RoiPoint[];
  activeInstance?: Sam3Instance | null;
  pointLabel: Sam3PointLabel;
  disabled?: boolean;
  showBoundingBox?: boolean;
  lassoResetKey: number;
  onPoint: (point: Sam3Point) => void;
  onLassoComplete: (polygon: Sam3RoiPoint[]) => void;
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("이미지를 표시하지 못했습니다."));
    image.src = src;
  });


export function Sam3Canvas({
  imageUrl,
  width,
  height,
  mode,
  points,
  lassoPolygon,
  activeInstance,
  pointLabel,
  disabled = false,
  showBoundingBox = false,
  lassoResetKey,
  onPoint,
  onLassoComplete,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);
  const draftRef = useRef<Sam3RoiPoint[]>([]);
  const [draftLasso, setDraftLasso] = useState<{
    resetKey: number;
    points: Sam3RoiPoint[];
  }>({ resetKey: lassoResetKey, points: [] });

  useEffect(() => {
    let cancelled = false;

    const draw = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return;

      const image = await loadImage(imageUrl);
      if (cancelled) return;
      context.clearRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      if (activeInstance?.mask_png_base64) {
        const mask = await loadImage(
          `data:image/png;base64,${activeInstance.mask_png_base64}`,
        );
        if (cancelled) return;

        const layer = document.createElement("canvas");
        layer.width = width;
        layer.height = height;
        const layerContext = layer.getContext("2d");
        if (layerContext) {
          layerContext.drawImage(mask, 0, 0, width, height);
          layerContext.globalCompositeOperation = "source-in";
          const accent =
            getComputedStyle(document.documentElement)
              .getPropertyValue("--seexr-primary")
              .trim() || "#89A7C2";
          layerContext.fillStyle = accent;
          layerContext.fillRect(0, 0, width, height);
          context.globalAlpha = 0.48;
          context.drawImage(layer, 0, 0);
          context.globalAlpha = 1;
        }

        if (showBoundingBox) {
          const [x1, y1, x2, y2] = activeInstance.box_xyxy;
          context.lineWidth = Math.max(2, Math.max(width, height) / 450);
          context.strokeStyle =
            getComputedStyle(document.documentElement)
              .getPropertyValue("--seexr-primary")
              .trim() || "#89A7C2";
          context.strokeRect(x1, y1, x2 - x1, y2 - y1);
        }
      }

      if (mode === "point") {
        const radius = Math.max(6, Math.max(width, height) / 120);
        for (const point of points) {
          context.beginPath();
          context.arc(point.x, point.y, radius, 0, Math.PI * 2);
          context.fillStyle = point.label === 1 ? "#89A7C2" : "#dc5a5a";
          context.fill();
          context.lineWidth = Math.max(2, radius / 3);
          context.strokeStyle = "#ffffff";
          context.stroke();
          context.fillStyle = "#ffffff";
          context.font = `700 ${Math.max(10, radius * 1.2)}px sans-serif`;
          context.textAlign = "center";
          context.textBaseline = "middle";
          context.fillText(point.label === 1 ? "+" : "−", point.x, point.y + 0.5);
        }
      }
    };

    void draw().catch((error) => {
      console.error("[SAM3 canvas] render failed", error);
    });

    return () => {
      cancelled = true;
    };
  }, [activeInstance, height, imageUrl, mode, points, showBoundingBox, width]);

  const coordinateFromPointer = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ): Sam3RoiPoint | null => {
    const canvas = canvasRef.current;
    if (!canvas || disabled) return null;
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return null;
    return {
      x: (event.clientX - bounds.left) * (canvas.width / bounds.width),
      y: (event.clientY - bounds.top) * (canvas.height / bounds.height),
    };
  };

  const currentDraft =
    draftLasso.resetKey === lassoResetKey ? draftLasso.points : [];
  const visiblePolygon = currentDraft.length > 0 ? currentDraft : lassoPolygon;
  const polygonPoints = visiblePolygon.map((point) => `${point.x},${point.y}`).join(" ");

  return (
    <div className="relative w-full">
      <canvas
        ref={canvasRef}
        className={`block h-auto max-h-[68vh] w-full touch-none select-none ${
          disabled
            ? "cursor-wait"
            : mode === "text"
              ? "cursor-default"
              : "cursor-crosshair"
        }`}
        onPointerDown={(event) => {
          if (disabled) return;
          if (mode === "point") {
            if (event.pointerType === "mouse" && ![0, 2].includes(event.button)) return;
            event.preventDefault();
            const coordinate = coordinateFromPointer(event);
            if (!coordinate) return;
            const label =
              event.pointerType === "mouse" && event.button === 2 ? 0 : pointLabel;
            onPoint({ ...coordinate, label });
            return;
          }

          if (mode !== "lasso") return;
          if (event.pointerType === "mouse" && event.button !== 0) return;
          const coordinate = coordinateFromPointer(event);
          if (!coordinate) return;
          event.preventDefault();
          drawingRef.current = true;
          pointerIdRef.current = event.pointerId;
          draftRef.current = [coordinate];
          setDraftLasso({
            resetKey: lassoResetKey,
            points: [clampSam3PointToImageBounds(coordinate, width, height)],
          });
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          if (
            mode !== "lasso" ||
            !drawingRef.current ||
            pointerIdRef.current !== event.pointerId
          ) {
            return;
          }
          const coordinate = coordinateFromPointer(event);
          if (!coordinate) return;
          const previous = draftRef.current[draftRef.current.length - 1];
          const minDistance = Math.max(width, height) / 600;
          if (
            previous &&
            Math.hypot(coordinate.x - previous.x, coordinate.y - previous.y) <
              minDistance
          ) {
            return;
          }
          const next = [...draftRef.current, coordinate].slice(-1024);
          draftRef.current = next;
          setDraftLasso({
            resetKey: lassoResetKey,
            points: next.map((point) =>
              clampSam3PointToImageBounds(point, width, height),
            ),
          });
        }}
        onPointerUp={(event) => {
          if (
            mode !== "lasso" ||
            !drawingRef.current ||
            pointerIdRef.current !== event.pointerId
          ) {
            return;
          }
          event.preventDefault();
          const coordinate = coordinateFromPointer(event);
          const next = coordinate
            ? [...draftRef.current, coordinate].slice(-1024)
            : draftRef.current;
          drawingRef.current = false;
          pointerIdRef.current = null;
          event.currentTarget.releasePointerCapture?.(event.pointerId);

          const sanitized = sanitizeSam3RoiPolygon(next, width, height);
          if (sanitized.length >= 3) {
            setDraftLasso({ resetKey: lassoResetKey, points: [] });
            draftRef.current = [];
            onLassoComplete(sanitized);
          } else {
            setDraftLasso({ resetKey: lassoResetKey, points: [] });
            draftRef.current = [];
          }
        }}
        onPointerCancel={() => {
          drawingRef.current = false;
          pointerIdRef.current = null;
          draftRef.current = [];
          setDraftLasso({ resetKey: lassoResetKey, points: [] });
        }}
        onContextMenu={(event) => event.preventDefault()}
        aria-label="SAM3 객체 선택 캔버스"
      />

      {mode === "lasso" && polygonPoints && (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          aria-hidden
        >
          <polygon
            points={polygonPoints}
            fill="rgba(137, 167, 194, 0.14)"
            stroke="#89A7C2"
            strokeWidth={Math.max(2, Math.max(width, height) / 450)}
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}
